/**
 * פרימיטיבים לגרפים של "תמונת מצב".
 *
 * הכל SVG שנכתב ידנית ולא ספריית גרפים: הפרויקט לא מכיל ספריית charting,
 * והוספת אחת בשביל מסך אחד גוררת ~200KB ל-bundle ועיצוב שצריך לעקוף. כל
 * הרכיבים כאן RTL — האיבר הראשון בסדרה יושב בקצה הימני של הציר.
 */

import { Fragment, ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CHART, fade, isLatinLabel, kMoney, nf, niceMax } from './chartTheme';
import styles from './Dashboard.module.css';

/* ============================ מדידת רוחב ============================ */

/**
 * רוחב הכרטיס בפועל — המידה שכל הגרפים מגיבים לה.
 *
 * ResizeObserver תופס את מה ש-window resize לא תופס: נעיצה ושחרור של סרגל
 * הצד משנים את רוחב הכרטיס בלי לשנות את רוחב החלון. ההפך גם נכון — סיבוב
 * מכשיר מגיע כ-resize/orientationchange, ובחלק מהסביבות ההודעה של
 * ResizeObserver מתעכבת או לא מגיעה כלל. שניהם רשומים, והשוואה מול המידה
 * הקודמת מונעת רינדורים מיותרים כששניהם מדווחים על אותו שינוי.
 */
const useWidth = (): [React.RefObject<HTMLDivElement | null>, number] => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let last = -1;
    const update = () => {
      const next = el.clientWidth;
      if (next !== last) {
        last = next;
        setWidth(next);
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  return [ref, width];
};

/* ============================ Tooltip ============================ */

export interface TipRow {
  name: string;
  value: string;
  color?: string;
}

interface TipState {
  x: number;
  y: number;
  title: string;
  rows: TipRow[];
}

const ChartTip = ({ tip }: { tip: TipState | null }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: 0, top: 0 });

  useEffect(() => {
    if (!tip || !ref.current) return;
    const { offsetWidth: w, offsetHeight: h } = ref.current;
    let left = tip.x - w / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - w - 8));
    const top = tip.y - h - 14 < 8 ? tip.y + 18 : tip.y - h - 14;
    setPos({ left, top });
  }, [tip]);

  if (!tip) return null;
  return (
    <div className={styles.tip} ref={ref} style={{ left: pos.left, top: pos.top }} role="status">
      <div className={styles.tipTitle}>{tip.title}</div>
      {tip.rows.map((r, i) => (
        <div className={styles.tipRow} key={i}>
          <span className={styles.tipName}>
            {r.color && <i style={{ background: r.color }} />}
            {r.name}
          </span>
          <b>{r.value}</b>
        </div>
      ))}
    </div>
  );
};

/**
 * Tooltip משותף לכל הגרפים, עם מגע ולא רק עכבר.
 *
 * במסך מגע אין hover: הנגיעה מגיעה כ-pointer event יחיד ואין אירוע יציאה
 * שיסגור את החלונית. לכן נגיעה סוגרת לבד אחרי כמה שניות, גלילה סוגרת מיד
 * (אחרת החלונית נשארת תלויה במקום שממנו הדף כבר זז), ונגיעה מחוץ לכל גרף
 * סוגרת גם היא. אירועי pointer מכסים עכבר, מגע ועט באותו קוד.
 */
const TOUCH_TIP_MS = 2600;

const useTip = () => {
  const [tip, setTip] = useState<TipState | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hide = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    setTip(null);
  }, []);

  const show = useCallback((e: { clientX: number; clientY: number; pointerType?: string }, title: string, rows: TipRow[]) => {
    if (timer.current) clearTimeout(timer.current);
    setTip({ x: e.clientX, y: e.clientY, title, rows });
    if (e.pointerType === 'touch') timer.current = setTimeout(() => setTip(null), TOUCH_TIP_MS);
  }, []);

  useEffect(() => {
    if (!tip) return;
    const onScroll = () => hide();
    const onDown = (e: PointerEvent) => {
      if (!(e.target as Element | null)?.closest?.('[data-chart-surface]')) hide();
    };
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('pointerdown', onDown);
    return () => {
      window.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('pointerdown', onDown);
    };
  }, [tip, hide]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  return { tip, show, hide };
};

/* ============================ מצבי קצה ============================ */

export const ChartEmpty = ({ height = 200, text = 'אין נתונים בתקופה הזו' }: { height?: number; text?: string }) => (
  <div className={styles.empty} style={{ height }}>
    {text}
  </div>
);

export const ChartSkeleton = ({ height = 200 }: { height?: number }) => <div className={styles.skeleton} style={{ height }} aria-hidden />;

/* ============================ גרף משולב ============================ */

export interface BarSeries {
  key: string;
  name: string;
  color: string;
}

export type ChartRow = { label: string; full?: string } & Record<string, string | number | undefined>;

interface ComboChartProps {
  data: ChartRow[];
  bars: BarSeries[];
  line?: { key: string; name: string; color: string; fmt?: (n: number) => string };
  height?: number;
  stacked?: boolean;
  fmtAxis?: (n: number) => string;
  fmtTip?: (n: number) => string;
  ariaLabel: string;
}

const num = (r: ChartRow, k: string): number => {
  const v = r[k];
  return typeof v === 'number' ? v : 0;
};

export const ComboChart = ({ data, bars, line, height = 250, stacked = false, fmtAxis = kMoney, fmtTip = nf, ariaLabel }: ComboChartProps) => {
  const [ref, w] = useWidth();
  const { tip, show, hide } = useTip();

  /*
   * המידות נגזרות מהרוחב הנמדד של הכרטיס, לא מרוחב החלון. סרגל הצד של האתר
   * הוא 76px כמסילה ו-288px פתוח, והמשתמש מנעץ ומשחרר אותו מתי שבא לו — כך
   * שאותו viewport נותן לכרטיס רוחב שונה לגמרי. ResizeObserver יודע את האמת.
   */
  const W = Math.max(w, 240);
  const compact = W < 420; // כרטיס ברוחב טלפון
  const snug = W < 640; // חצי רוחב בטאבלט
  const H = compact ? Math.round(height * 0.88) : height;
  const padT = compact ? 12 : 16;
  const padB = compact ? 22 : 26;
  const padS = compact ? 44 : 54; // צד ההתחלה = ימין
  // בכרטיס צר הציר המשני ויתר עליו: הקו נשאר, התוויות שלו יורדות והשטח חוזר לגרף
  const showLineAxis = !!line && !compact;
  const padE = line ? (showLineAxis ? (snug ? 46 : 58) : 12) : compact ? 8 : 14;
  const plotW = W - padS - padE;
  const plotH = H - padT - padB;
  const n = data.length;
  const band = n > 0 ? plotW / n : plotW;
  const gridCount = compact ? 3 : 4;
  const axisFont = compact ? 10 : 11;

  const maxBar = Math.max(1, ...data.map((d) => (stacked ? bars.reduce((s, b) => s + num(d, b.key), 0) : Math.max(...bars.map((b) => num(d, b.key))))));
  const yTop = niceMax(maxBar * 1.06);
  const y = (v: number) => padT + plotH * (1 - v / yTop);
  const cx = (i: number) => W - padS - (i + 0.5) * band;

  // התקרה הישנה (22px) נחשבת רק בטווח יומי, שם ה-band ממילא צר ממנה. בטווח עם
  // מעט עמודות (כמו תצוגה שנתית, 6-7 עמודות) ה-band רחב, וההתקרה הנמוכה הייתה
  // משאירה עמודות דקות עם המון רווח ריק סביבן - ולכן נראה "לא מסודר".
  const bw = Math.min(stacked ? band * 0.56 : (band * 0.66) / bars.length, stacked ? 34 : 60);
  // כשהעמודות צפופות הרווח ביניהן אוכל אותן — מצמצמים אותו במקום להעלים אותן
  const fill = band < 12 ? 0.96 : 0.86;

  const lineVals = line ? data.map((d) => num(d, line.key)) : [];
  // ריווח לפי טווח הערכים ולא כפל בקבוע: כפל ב-0.92 מקרב ערך שלילי לאפס, והקו
  // (יתרה או שינוי מצטבר שירד מתחת לאפס) היה נחתך מתחת לגרף.
  const lineMin = line ? Math.min(...lineVals) : 0;
  const lineMax = line ? Math.max(...lineVals) : 1;
  const linePad = Math.max(Math.abs(lineMax - lineMin), Math.abs(lineMax), 1) * 0.06;
  const lo = lineMin - linePad;
  const hi = lineMax + linePad;
  const ly = (v: number) => padT + plotH * 0.94 * (1 - (v - lo) / Math.max(1, hi - lo)) + plotH * 0.03;

  const maxLabels = compact ? 4 : snug ? 7 : 14;
  const labelStep = Math.max(1, Math.ceil(n / maxLabels));

  const showRow = (e: React.PointerEvent, d: ChartRow) =>
    show(e, d.full ?? d.label, [
      ...bars.map((bar) => ({ name: bar.name, color: bar.color, value: fmtTip(num(d, bar.key)) })),
      ...(line ? [{ name: line.name, color: line.color, value: (line.fmt ?? kMoney)(num(d, line.key)) }] : []),
    ]);

  return (
    <div className={styles.chartWrap} ref={ref} data-chart-surface>
      {w > 0 && n > 0 && (
        <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={ariaLabel}>
          {/* רשת וציר ערכים */}
          {Array.from({ length: gridCount + 1 }, (_, g) => {
            const v = (yTop * g) / gridCount;
            return (
              <g key={g}>
                <line x1={padE} x2={W - padS} y1={y(v)} y2={y(v)} stroke={CHART.grid} strokeWidth={1} />
                <text x={W - padS + 6} y={y(v) + 4} fill={CHART.ink3} fontSize={axisFont} fontFamily="Heebo, Assistant, sans-serif">
                  {fmtAxis(v)}
                </text>
              </g>
            );
          })}

          {/* עמודות */}
          {data.map((d, i) => {
            if (stacked) {
              let acc = 0;
              return (
                <g key={i}>
                  {bars.map((b) => {
                    const v = num(d, b.key);
                    if (v <= 0) return null;
                    const y1 = y(acc + v);
                    const hgt = Math.max(1, y(acc) - y1);
                    acc += v;
                    return <rect key={b.key} x={cx(i) - bw / 2} y={y1} width={bw} height={hgt} fill={b.color} rx={3} />;
                  })}
                </g>
              );
            }
            return (
              <g key={i}>
                {bars.map((b, bi) => {
                  const v = num(d, b.key);
                  const x = cx(i) + (bars.length * bw) / 2 - (bi + 1) * bw + (bw - bw * fill) / 2;
                  const w2 = bw * fill;
                  const yy = y(v);
                  const hgt = Math.max(1.5, y(0) - yy);
                  const r = Math.min(4, w2 * 0.45, hgt);
                  return (
                    <path
                      key={b.key}
                      fill={b.color}
                      d={`M${x},${yy + hgt} L${x},${yy + r} Q${x},${yy} ${x + r},${yy} L${x + w2 - r},${yy} Q${x + w2},${yy} ${x + w2},${yy + r} L${x + w2},${yy + hgt} Z`}
                    />
                  );
                })}
              </g>
            );
          })}

          {/* קו על ציר משני */}
          {line && (
            <g>
              <polyline
                points={data.map((d, i) => `${cx(i)},${ly(num(d, line.key))}`).join(' ')}
                fill="none"
                stroke={line.color}
                strokeWidth={2.2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {data.map((d, i) =>
                n <= 16 || i === 0 || i === n - 1 ? (
                  <circle
                    key={i}
                    cx={cx(i)}
                    cy={ly(num(d, line.key))}
                    r={i === n - 1 ? 4 : 2.6}
                    fill={i === n - 1 ? line.color : CHART.surface}
                    stroke={line.color}
                    strokeWidth={2}
                  />
                ) : null
              )}
              {showLineAxis &&
                [hi, (hi + lo) / 2, lo].map((v, k) => (
                  <text
                    key={k}
                    x={padE - 6}
                    y={ly(v) + 4}
                    fill={fade(line.color, 0.85)}
                    fontSize={10.5}
                    textAnchor="end"
                    fontFamily="Heebo, Assistant, sans-serif"
                  >
                    {(line.fmt ?? kMoney)(v)}
                  </text>
                ))}
            </g>
          )}

          {/* תוויות ציר X */}
          {data.map((d, i) =>
            i % labelStep === 0 || i === n - 1 ? (
              <text
                key={i}
                x={cx(i)}
                y={H - 8}
                fill={CHART.ink3}
                fontSize={axisFont}
                textAnchor="middle"
                fontFamily="Assistant, sans-serif"
                style={isLatinLabel(d.label) ? { direction: 'ltr' } : undefined}
              >
                {d.label}
              </text>
            ) : null
          )}

          {/* אזורי hover */}
          {data.map((d, i) => (
            <rect
              key={`h${i}`}
              x={cx(i) - band / 2}
              y={padT}
              width={band}
              height={plotH}
              fill="transparent"
              onPointerMove={(e) => showRow(e, d)}
              onPointerDown={(e) => showRow(e, d)}
              onPointerLeave={(e) => {
                if (e.pointerType !== 'touch') hide();
              }}
            />
          ))}
        </svg>
      )}
      <ChartTip tip={tip} />
    </div>
  );
};

/* ============================ דונאט ============================ */

export interface DonutItem {
  name: string;
  value: number;
  color: string;
}

export const Donut = ({ items, centerLabel = 'סה״כ', fmt = kMoney }: { items: DonutItem[]; centerLabel?: string; fmt?: (n: number) => string }) => {
  const { tip, show, hide } = useTip();
  const total = items.reduce((s, i) => s + i.value, 0);
  if (total <= 0) return <ChartEmpty height={160} />;

  const size = 148;
  const R = 66;
  const r = 44;
  const c = size / 2;
  let a0 = -Math.PI / 2;

  const slices = items.map((it) => {
    const fraction = it.value / total;
    const a1 = a0 + fraction * Math.PI * 2;
    const big = a1 - a0 > Math.PI ? 1 : 0;
    const P = (ang: number, rad: number) => [c + rad * Math.cos(ang), c + rad * Math.sin(ang)];
    const [x1, y1] = P(a0, R);
    const [x2, y2] = P(a1, R);
    const [x3, y3] = P(a1, r);
    const [x4, y4] = P(a0, r);
    a0 = a1;
    return {
      it,
      /**
       * פרוסה שהיא הדונאט כולו נצבעת כטבעת ולא כקשת.
       *
       * קשת שנקודת ההתחלה שלה זהה לנקודת הסיום היא ניוונית לפי מפרט SVG והדפדפן
       * פשוט לא מצייר אותה — ולכן קרן שכל יתרתה מתרומה אחת, או קרן שכל תרומותיה
       * בהעברה בנקאית, הציגו מקום ריק במקום עוגה שלמה.
       */
      full: fraction >= 0.9995,
      d: `M${x1},${y1} A${R},${R} 0 ${big} 1 ${x2},${y2} L${x3},${y3} A${r},${r} 0 ${big} 0 ${x4},${y4} Z`,
    };
  });

  const showSlice = (e: React.PointerEvent, it: DonutItem) =>
    show(e, it.name, [
      { name: 'סכום', color: it.color, value: fmt(it.value) },
      { name: 'חלק', value: Math.round((it.value / total) * 100) + '%' },
    ]);

  return (
    <div className={styles.donutWrap} data-chart-surface>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={centerLabel}>
        {slices.map((s, i) =>
          s.full ? (
            <circle
              key={i}
              cx={c}
              cy={c}
              r={(R + r) / 2}
              fill="none"
              stroke={s.it.color}
              strokeWidth={R - r}
              onPointerMove={(e) => showSlice(e, s.it)}
              onPointerDown={(e) => showSlice(e, s.it)}
              onPointerLeave={(e) => {
                if (e.pointerType !== 'touch') hide();
              }}
            />
          ) : (
            <path
              key={i}
              d={s.d}
              fill={s.it.color}
              stroke={CHART.surface}
              strokeWidth={2.5}
              onPointerMove={(e) => showSlice(e, s.it)}
              onPointerDown={(e) => showSlice(e, s.it)}
              onPointerLeave={(e) => {
                if (e.pointerType !== 'touch') hide();
              }}
            />
          )
        )}
        <text x={c} y={c - 2} textAnchor="middle" fill={CHART.ink} fontSize={17} fontWeight={700} fontFamily="Heebo, Assistant, sans-serif">
          {fmt(total)}
        </text>
        <text x={c} y={c + 15} textAnchor="middle" fill={CHART.ink3} fontSize={11} fontFamily="Assistant, sans-serif">
          {centerLabel}
        </text>
      </svg>
      <div className={styles.donutLegend}>
        {items.map((it, i) => (
          <div className={styles.dlRow} key={i}>
            <i style={{ background: it.color }} />
            <span className={styles.dlName}>{it.name}</span>
            <span className={styles.dlValue}>{fmt(it.value)}</span>
            <span className={styles.dlPct}>{Math.round((it.value / total) * 100)}%</span>
          </div>
        ))}
      </div>
      <ChartTip tip={tip} />
    </div>
  );
};

/* ============================ רשימה מדורגת ============================ */

export interface RankItem {
  name: string;
  value: number;
  sub?: string;
  color: string;
}

export const RankList = ({ items, fmt = kMoney }: { items: RankItem[]; fmt?: (n: number) => string }) => {
  if (!items.length) return <ChartEmpty height={140} />;
  // לפחות 1: אם כל הערכים אפס, חלוקה באפס הייתה נותנת רוחב NaN.
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <div className={styles.rank}>
      {items.map((it, i) => (
        <div className={styles.rankRow} key={i}>
          <span className={styles.rankName}>
            {it.name}
            {it.sub && <span className={styles.rankSub}> · {it.sub}</span>}
          </span>
          <span className={styles.rankValue}>{fmt(it.value)}</span>
          <span className={styles.rankBar}>
            <i style={{ width: `${Math.max(3, (it.value / max) * 100)}%`, background: it.color }} />
          </span>
        </div>
      ))}
    </div>
  );
};

/* ============================ מפת חום ============================ */

export const Heatmap = ({
  rows,
  cols,
  values,
  color,
  metric,
  fmt,
}: {
  rows: string[];
  cols: string[];
  values: number[][];
  color: string;
  metric: string;
  fmt: (n: number) => string;
}) => {
  const { tip, show, hide } = useTip();
  const max = Math.max(1, ...values.flat());
  return (
    <div className={styles.chartWrap}>
      <div className={styles.hmScroll}>
      <div className={styles.hmGrid} style={{ gridTemplateColumns: `auto repeat(${cols.length}, 1fr)` }}>
        <div className={styles.hmCorner} />
        {cols.map((c) => (
          <div className={`${styles.hmLabel} ${styles.hmColHead}`} key={c}>
            {c}
          </div>
        ))}
        {rows.map((rowName, ri) => (
          <Fragment key={`row-${ri}`}>
            <div className={styles.hmLabel} style={{ paddingInlineEnd: 6 }}>
              {rowName}
            </div>
            {values[ri].map((v, ci) => (
              <div
                key={`${ri}-${ci}`}
                className={styles.hmCell}
                style={{ background: v <= 0 ? CHART.sunk : fade(color, 0.12 + (v / max) * 0.88) }}
                onPointerMove={(e) => show(e, `${rowName} · ${cols[ci]}`, [{ name: metric, value: fmt(v), color }])}
                onPointerDown={(e) => show(e, `${rowName} · ${cols[ci]}`, [{ name: metric, value: fmt(v), color }])}
                onPointerLeave={(e) => {
                  if (e.pointerType !== 'touch') hide();
                }}
              />
            ))}
          </Fragment>
        ))}
      </div>
      </div>
      <div className={styles.hmScale}>
        <span>נמוך</span>
        {[0.14, 0.38, 0.62, 0.86].map((a) => (
          <i key={a} style={{ background: fade(color, a) }} />
        ))}
        <i style={{ background: color }} />
        <span>גבוה</span>
      </div>
      <ChartTip tip={tip} />
    </div>
  );
};

/* ============================ מד ============================ */

export const Gauge = ({
  value,
  max,
  display,
  unit,
  color,
  foot,
}: {
  value: number;
  max: number;
  display: string;
  unit?: string;
  color: string;
  foot?: ReactNode;
}) => {
  const W = 190;
  const H = 128;
  const cx = W / 2;
  const cy = 104;
  const R = 74;
  const A0 = Math.PI * 0.92;
  const A1 = Math.PI * 2.08;
  const arc = (from: number, to: number) => {
    const P = (a: number) => [cx + R * Math.cos(a), cy + R * Math.sin(a)];
    const [x1, y1] = P(from);
    const [x2, y2] = P(to);
    return `M${x1},${y1} A${R},${R} 0 ${to - from > Math.PI ? 1 : 0} 1 ${x2},${y2}`;
  };
  const frac = Math.max(0, Math.min(1, max > 0 ? value / max : 0));

  return (
    <div className={styles.chartWrap}>
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${display} ${unit ?? ''}`}>
        <path d={arc(A0, A1)} fill="none" stroke={CHART.sunk} strokeWidth={13} strokeLinecap="round" />
        {frac > 0 && <path d={arc(A0, A0 + (A1 - A0) * frac)} fill="none" stroke={color} strokeWidth={13} strokeLinecap="round" />}
        <text x={cx} y={cy - 14} textAnchor="middle" fill={CHART.ink} fontSize={30} fontWeight={700} fontFamily="Heebo, Assistant, sans-serif">
          {display}
        </text>
        {unit && (
          <text x={cx} y={cy + 8} textAnchor="middle" fill={CHART.ink3} fontSize={12} fontFamily="Assistant, sans-serif">
            {unit}
          </text>
        )}
      </svg>
      {foot && <div className={styles.gaugeFoot}>{foot}</div>}
    </div>
  );
};

/* ============================ ספארקליין ============================ */

export const Sparkline = ({ values, color, height = 26 }: { values: number[]; color: string; height?: number }) => {
  if (values.length < 2) return null;
  const w = 100;
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const X = (i: number) => w - (i / (values.length - 1)) * w;
  const Y = (v: number) => height - ((v - lo) / Math.max(1e-6, hi - lo)) * (height - 4) - 2;
  const pts = values.map((v, i) => `${X(i)},${Y(v)}`).join(' ');
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" aria-hidden>
      <polygon points={`${w},${height} ${pts} 0,${height}`} fill={fade(color, 0.1)} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};
